import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './PaidAmounts.css';
import fetchData from '../../functions/fetchData';
import CardComponent from '../../components/cards/CardComponent';
import FloatingNavModal from '../../components/floatingbuttons/FloatingNavModal';
import { 
  FaDollarSign, 
  FaCreditCard, 
  FaUniversity, 
  FaHome,
  FaCheck,
  FaSpinner,
  FaReceipt
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';

const PaidAmounts = () => {
  console.log('PaidAmounts component rendering...');
  const navigate = useNavigate();
  
  const [paidRecords, setPaidRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState({
    totalPaidAmount: 0,
    totalRecords: 0,
    cashAmount: 0,
    cardAmount: 0,
    transferAmount: 0,
    cashCount: 0,
    cardCount: 0,
    transferCount: 0
  });

  // Get user information for filtering
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const userId = localStorage.getItem('userid') || sessionStorage.getItem('userid');
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  const isAuthenticated = !!token;
  const isCashier = userType === 'cashier' || userType === 'admin';

  // Fetch paid records where balance = 0 and userid matches logged in user
  const fetchPaidRecords = useCallback(async () => {
    console.log('Fetching paid records with conditions:', { userId, selectedDate });
    setLoading(true);
    try {
      const whereCondition = {
        balance: '0',
        userid: userId,
        dte: selectedDate
      };
      
      console.log('Calling fetchData with whereCondition:', whereCondition);
      const paidData = await fetchData('collection', null, 'id DESC', whereCondition);
      console.log('Received paid data:', paidData);
      
      setPaidRecords(paidData || []);
      
      // Calculate summary
      if (paidData && paidData.length > 0) {
        const summaryData = paidData.reduce((acc, record) => {
          const paidAmount = parseFloat(record.paidamount) || 0;
          const mode = record.mode;
          
          acc.totalPaidAmount += paidAmount;
          acc.totalRecords += 1;
          
          if (mode === 'cash') {
            acc.cashAmount += paidAmount;
            acc.cashCount += 1;
          } else if (mode === 'card') {
            acc.cardAmount += paidAmount;
            acc.cardCount += 1;
          } else if (mode === 'btransfer') {
            acc.transferAmount += paidAmount;
            acc.transferCount += 1;
          }
          
          return acc;
        }, {
          totalPaidAmount: 0,
          totalRecords: 0,
          cashAmount: 0,
          cardAmount: 0,
          transferAmount: 0,
          cashCount: 0,
          cardCount: 0,
          transferCount: 0
        });
        
        console.log('Calculated summary:', summaryData);
        setSummary(summaryData);
      } else {
        console.log('No paid records found, setting empty summary');
        setSummary({
          totalPaidAmount: 0,
          totalRecords: 0,
          cashAmount: 0,
          cardAmount: 0,
          transferAmount: 0,
          cashCount: 0,
          cardCount: 0,
          transferCount: 0
        });
      }
      
    } catch (error) {
      console.error('Error fetching paid records:', error);
      toast.error(`Failed to load paid records: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedDate]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!isCashier) {
      toast.error('Access denied. Only cashiers can access this page.');
      navigate('/dashboard/cashier');
      return;
    }

    fetchPaidRecords();
  }, [isAuthenticated, isCashier, navigate, fetchPaidRecords]);

  if (!isAuthenticated) {
    console.log('User not authenticated, redirecting...');
    return null;
  }

  if (!isCashier) {
    console.log('User not authorized as cashier');
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <h3>Access Denied</h3>
          <p>Only cashiers and admins can access this page.</p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard/cashier')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  console.log('Rendering PaidAmounts component with data:', { paidRecords, summary, loading });

  return (
    <div className="paid-amounts">
      <ToastContainer />
      
      <div className="container-fluid">
        {/* Debug Info */}
        <div className="alert alert-info mb-3">
          <strong>Debug Info:</strong><br/>
          User Type: {userType}<br/>
          User ID: {userId}<br/>
          Is Authenticated: {isAuthenticated ? 'Yes' : 'No'}<br/>
          Is Cashier: {isCashier ? 'Yes' : 'No'}<br/>
          Selected Date: {selectedDate}<br/>
          Records Count: {paidRecords.length}<br/>
          Loading: {loading ? 'Yes' : 'No'}
        </div>

        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <CardComponent>
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <div className="d-flex align-items-center">
                  <button 
                    className="btn btn-outline-primary me-3"
                    onClick={() => navigate('/collection/barcode-payment')}
                  >
                    <FaHome /> Back to Payment
                  </button>
                  <h2 className="mb-0">
                    <FaReceipt /> Paid Amounts
                  </h2>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <div>
                    <label className="form-label mb-1">Filter by Date:</label>
                    <input
                      type="date"
                      className="form-control"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </div>
                  <button 
                    className="btn btn-primary"
                    onClick={fetchPaidRecords}
                    disabled={loading}
                  >
                    {loading ? <FaSpinner className="fa-spin" /> : <FaCheck />} Refresh
                  </button>
                </div>
              </div>
              <p className="text-muted">View all completed payments processed by you</p>
            </CardComponent>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <CardComponent title="Total Paid" headerColor="success" pull="left">
              <div className="metric-content">
                <div className="metric-value text-success">
                  ₹{summary.totalPaidAmount.toLocaleString()}
                </div>
                <div className="text-muted">{summary.totalRecords} transactions</div>
              </div>
            </CardComponent>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <CardComponent title="Cash Payments" headerColor="success" pull="left">
              <div className="payment-summary">
                <div className="d-flex align-items-center">
                  <FaDollarSign className="text-success me-2" />
                  <span className="fw-bold">₹{summary.cashAmount.toLocaleString()}</span>
                </div>
                <small className="text-muted">{summary.cashCount} transactions</small>
              </div>
            </CardComponent>
          </div>

          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <CardComponent title="Card Payments" headerColor="primary" pull="left">
              <div className="payment-summary">
                <div className="d-flex align-items-center">
                  <FaCreditCard className="text-primary me-2" />
                  <span className="fw-bold">₹{summary.cardAmount.toLocaleString()}</span>
                </div>
                <small className="text-muted">{summary.cardCount} transactions</small>
              </div>
            </CardComponent>
          </div>

          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <CardComponent title="Bank Transfers" headerColor="info" pull="left">
              <div className="payment-summary">
                <div className="d-flex align-items-center">
                  <FaUniversity className="text-info me-2" />
                  <span className="fw-bold">₹{summary.transferAmount.toLocaleString()}</span>
                </div>
                <small className="text-muted">{summary.transferCount} transactions</small>
              </div>
            </CardComponent>
          </div>
        </div>

        {/* Paid Records Table */}
        <div className="row">
          <div className="col-12">
            <CardComponent
              title={`Completed Payments (${paidRecords.length} records)`}
              headerColor="success"
              pull="left"
            >
              {loading ? (
                <div className="text-center py-4">
                  <FaSpinner className="fa-spin me-2" />
                  Loading paid records...
                </div>
              ) : paidRecords.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <FaCheck size={48} className="mb-3 text-success" />
                  <h5>No Completed Payments</h5>
                  <p>No fully paid records found for the selected date.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead className="table-success">
                      <tr>
                        <th>Bill No.</th>
                        <th>Table</th>
                        <th>Time</th>
                        <th>Net Amount</th>
                        <th>Paid Amount</th>
                        <th>Payment Mode</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paidRecords.map((record, index) => (
                        <tr key={record.id || index}>
                          <td>
                            <strong className="text-success">{record.barbill}</strong>
                          </td>
                          <td>Table {record.tables}</td>
                          <td>{record.tme ? record.tme.split('.')[0] : ''}</td>
                          <td>₹{record.netamount}</td>
                          <td>₹{record.paidamount}</td>
                          <td>
                            <span className={`badge ${
                              record.mode === 'cash' ? 'bg-success' : 
                              record.mode === 'card' ? 'bg-primary' : 
                              record.mode === 'btransfer' ? 'bg-info' : 'bg-secondary'
                            }`}>
                              {record.mode === 'cash' && <FaDollarSign className="me-1" />}
                              {record.mode === 'card' && <FaCreditCard className="me-1" />}
                              {record.mode === 'btransfer' && <FaUniversity className="me-1" />}
                              {record.mode ? record.mode.toUpperCase() : 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-success">
                              <FaCheck className="me-1" />
                              PAID
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-success">
                        <td colSpan="3" className="text-end fw-bold">
                          Total Amount Collected:
                        </td>
                        <td className="fw-bold">
                          ₹{paidRecords.reduce((total, record) => total + (parseFloat(record.netamount) || 0), 0).toLocaleString()}
                        </td>
                        <td className="fw-bold">
                          <span className="badge bg-success fs-6">
                            ₹{summary.totalPaidAmount.toLocaleString()}
                          </span>
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardComponent>
          </div>
        </div>
      </div>

      {/* Floating Navigation Modal */}
      <FloatingNavModal currentPage="paid" />
    </div>
  );
};

export default PaidAmounts;